import React, { useState, useRef, useEffect, useCallback } from 'react';
import { jsPDF } from 'jspdf';
import './App.css';

// Validation rules
const validationRules = {
    landlordName: {
        required: true,
        minLength: 2
    },
    landlordPhone: {
        required: true,
        pattern: /^\+?[\d\s-()]{10,}$/
    },
    landlordEmail: {
        required: true,
        email: true
    },
    propertyAddress: {
        required: true,
        minLength: 5
    },
    tenantNames: {
        required: true,
        minLength: 2
    },
    rentAmount: {
        required: true,
        numeric: true,
        min: 0
    },
    leaseStart: {
        required: true,
        date: true
    },
    leaseEnd: {
        required: true,
        date: true
    },
    noticeDate: {
        required: true,
        date: true
    },
    moveOutDate: {
        required: true,
        date: true
    }
};

// Dataset
const dataset = {
    // ... existing dataset ...
};

const App = () => {
    // Add US states array
    const usStates = [
        'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
        'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
        'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
        'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
        'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
    ];

    // Add city suggestions state
    const [citySuggestions, setCitySuggestions] = useState({
        landlord: [],
        tenant: [],
        property: []
    });

    // Add debounced city search function
    const searchCities = async (query, section) => {
        if (!query || query.length < 2) {
            setCitySuggestions(prev => ({ ...prev, [section]: [] }));
            return;
        }

        try {
            const response = await fetch(
                `https://api.openweathermap.org/geo/1.0/direct?q=${encodeURIComponent(query)},US&limit=5&appid=YOUR_API_KEY`
            );
            const data = await response.json();
            
            setCitySuggestions(prev => ({
                ...prev,
                [section]: data.map(city => ({
                    name: city.name,
                    state: city.state
                }))
            }));
        } catch (error) {
            console.error('Error fetching cities:', error);
        }
    };

    // Debounce the search function
    const debouncedSearch = useCallback(
        debounce((query, section) => searchCities(query, section), 300),
        []
    );

    const [formData, setFormData] = useState({
        landlord: {
            isRepresentative: true,
            name: '',
            representativeName: '',
            address: '',
            city: '',
            state: '',
            zip: '',
            phone: '',
            email: ''
        },
        tenant: {
            names: '',
            address: '',
            city: '',
            state: '',
            zip: ''
        },
        property: {
            address: '',
            city: '',
            state: '',
            zip: ''
        },
        lease: {
            startDate: '',
            endDate: '',
            rentAmount: '',
            rentDueDate: ''
        },
        violation: {
            reason: '',
            description: '',
            otherDescription: ''
        },
        notice: {
            date: new Date().toISOString().split('T')[0],
            details: ''
        }
    });

    const [currentStep, setCurrentStep] = useState(1);
    const [previewContent, setPreviewContent] = useState('');
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const letterPreviewRef = useRef(null);

    // Add useEffect to prefill form on component mount
    useEffect(() => {
        prefillForm();
    }, []);

    // Update handleInputChange to handle city suggestions
    const handleInputChange = (section, field, value) => {
        setFormData(prev => {
            const newData = {
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value
                }
            };

            // If changing city, trigger city search
            if (field === 'city') {
                debouncedSearch(value, section);
            }

            // Update notice details based on violation reason only when reason changes
            if (section === 'violation' && field === 'reason') {
                let details = '';
                switch (value) {
                    case 'nonpayment':
                        details = `This notice is being served due to non-payment of rent. The total amount owed is $${prev.lease.rentAmount || '[Amount]'}. The rent was due on ${prev.lease.rentDueDate || '[Date]'}. Please pay the full amount within 3 days or vacate the premises. If you fail to pay or vacate, legal action will be taken.`;
                        break;
                    case 'violations':
                        details = `This notice is being served due to violations of the lease agreement. The following violations have been documented: ${prev.violation.description || '[List specific violations]'}. Please correct these violations within 3 days or vacate the premises. If you fail to cure the violations or vacate, legal action will be taken.`;
                        break;
                    case 'damage':
                        details = `This notice is being served due to property damage. The following damage has been documented: ${prev.violation.description || '[Describe damage]'}. Please repair the damage or arrange for professional repairs within 3 days, or vacate the premises. If you fail to repair the damage or vacate, legal action will be taken.`;
                        break;
                    case 'illegal':
                        details = `This notice is being served due to illegal activity on the premises. The following illegal activities have been documented: ${prev.violation.description || '[Describe illegal activities]'}. This is a serious violation of the lease agreement. Please vacate the premises immediately. If you fail to vacate, legal action will be taken.`;
                        break;
                    case 'expiration':
                        details = `This notice is being served due to lease expiration. The lease term ended on ${prev.lease.endDate || '[Date]'}. Please vacate the premises by ${prev.notice.terminationDate || '[Date]'}. If you fail to vacate, legal action will be taken.`;
                        break;
                    case 'occupancy':
                        details = `This notice is being served as the owner wishes to occupy the property. This Notice required you to vacate from the property on or before ${prev.notice.terminationDate || '[Date]'}. If you fail to vacate, legal action will be taken.`;
                        break;
                    case 'other':
                        details = prev.violation.otherDescription || 'Please provide specific details about the reason for eviction and required actions.';
                        break;
                }
                // Always update notice details when violation reason changes
                newData.notice.details = details;
            }

            // Update notice details when otherDescription changes for 'other' reason
            if (section === 'violation' && field === 'otherDescription' && prev.violation.reason === 'other') {
                newData.notice.details = value || 'Please provide specific details about the reason for eviction and required actions.';
            }

            // If updating notice details directly, always use the new value
            if (section === 'notice' && field === 'details') {
                newData.notice.details = value;
            }

            return newData;
        });

        // Update preview content when form data changes
        setPreviewContent(generateLetterContent());
    };

    // Add function to handle city selection
    const handleCitySelect = (section, city) => {
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                city: city.name,
                state: city.state
            }
        }));
        setCitySuggestions(prev => ({ ...prev, [section]: [] }));
    };

    // Update useEffect to always generate preview content
    useEffect(() => {
        setPreviewContent(generateLetterContent());
    }, [formData]);

    const generateLetterContent = () => {
        const allTenantNames = formData.tenant.names.split('\n').map(name => name.trim()).join(', ');
        const propertyAddress = `${formData.property.address}, ${formData.property.city}, ${formData.property.state} ${formData.property.zip}`;

        // Use the notice details directly if they exist, otherwise use the default template
        const violationDetails = formData.notice.details || (() => {
            switch (formData.violation.reason) {
                case 'nonpayment':
                    return 'This notice is being served due to non-payment of rent.';
                case 'violations':
                    return formData.violation.description;
                case 'damage':
                    return `This notice is being served due to property damage. ${formData.violation.description}`;
                case 'illegal':
                    return 'This notice is being served due to illegal activity on the premises.';
                case 'expiration':
                    return `This notice is being served due to lease expiration. The lease term ended on ${formData.lease.endDate}.`;
                case 'occupancy':
                    return 'This notice is being served as the owner wishes to occupy the property.';
                case 'other':
                    return formData.violation.otherDescription;
                default:
                    return '';
            }
        })();

        // Create the main content paragraph based on whether it's a representative or not
        const mainContent = formData.landlord.isRepresentative ? 
            `I represent ${formData.landlord.name}, the Owners (hereinafter "my client(s)") of the premises located at ${propertyAddress} (the "Property"), which you currently occupy under a rental agreement.` :
            `I am the Owner of the premises located at ${propertyAddress} (the "Property"), which you currently occupy under a rental agreement.`;

        // Determine the signature name based on whether there's a representative
        const signatureName = formData.landlord.isRepresentative ? 
            (formData.landlord.representativeName || formData.landlord.name) : 
            formData.landlord.name;

        // Create the second paragraph based on whether it's a representative or not
        const secondParagraph = formData.landlord.isRepresentative ?
            `My client has attempted to resolve this matter amicably, however, due to your failure to vacate, my client is forced to take legal action to take possession of the property. Currently, this eviction lawsuit has not been filed and is not public record.` :
            `I have attempted to resolve this matter amicably, however, due to your failure to vacate, I am forced to take legal action to take possession of the property. Currently, this eviction lawsuit has not been filed and is not public record.`;

        // Create the third paragraph based on whether it's a representative or not
        const thirdParagraph = formData.landlord.isRepresentative ?
            `Please note that this letter is intended in good faith to resolve the issues surrounding your tenancy. Currently your failure to vacate as required is strictly a matter with your landlord, however, should we not be able to resolve this matter with an agreement to vacate, I will advise my client to move full speed ahead, file the enclosed lawsuit with the Courts and request the Court issue an Order for your removal from the property, resulting in an eviction on your record.` :
            `Please note that this letter is intended in good faith to resolve the issues surrounding your tenancy. Currently your failure to vacate as required is strictly a matter between us, however, should we not be able to resolve this matter with an agreement to vacate, I will move full speed ahead, file the enclosed lawsuit with the Courts and request the Court issue an Order for your removal from the property, resulting in an eviction on your record.`;

        return `
            <div class="letter-content">
                <h1 style="text-align: center; font-size: 24px; margin-bottom: 10px;">NOTICE TO VACATE</h1>
                <p style="text-align: center; font-size: 12px; margin-bottom: 20px;">Pursuant to State Eviction Laws</p>
                <hr style="border: 1px solid #ccc; margin: 20px 0;">
                <p style="margin: 0 0 20px 0;">Date: ${formData.notice.date}</p>
                <p style="margin: 0 0 20px 0;">To: ${allTenantNames}</p>
                <p style="margin: 0 0 20px 0;">Property Address: ${propertyAddress}</p>
                <p style="margin: 0 0 20px 0;">Dear ${allTenantNames.split(',')[0]},</p>
                <p style="margin: 0 0 20px 0; line-height: 1.6;">${mainContent}</p>
                <p style="margin: 0 0 20px 0; line-height: 1.6;">${violationDetails}</p>
                <p style="margin: 0 0 20px 0; line-height: 1.6;">${secondParagraph}</p>
                <p style="margin: 0 0 20px 0; line-height: 1.6;">${thirdParagraph}</p>
                <p style="margin: 0 0 20px 0; line-height: 1.6;">If you wish to discuss this matter, please respond to my office by the deadline stated in the Summons.</p>
                <p style="margin: 0 0 20px 0; line-height: 1.6; font-weight: bold;">${formData.landlord.isRepresentative ? 'IF YOU HAVE ANY QUESTIONS, PLEASE DO NOT HESITATE TO CONTACT OUR OFFICE.' : 'IF YOU HAVE ANY QUESTIONS, PLEASE DO NOT HESITATE TO CONTACT ME.'}</p>
                <p style="margin: 0 0 20px 0; line-height: 1.6;">Thank you for your prompt attention to this matter,</p>
                <p style="margin: 0 0 20px 0; line-height: 1.6;">Sincerely,</p>
                <p style="margin: 0 0 10px 0; line-height: 1.6;">_____________________________</p>
                <p style="margin: 0 0 20px 0; line-height: 1.6;">${signatureName}</p>
                <p style="margin: 0 0 20px 0; line-height: 1.6;">${formData.landlord.address}<br>${formData.landlord.city}, ${formData.landlord.state} ${formData.landlord.zip}</p>
                <p style="margin: 0 0 20px 0; line-height: 1.6;">Phone: ${formData.landlord.phone}<br>Email: ${formData.landlord.email}</p>
                <div style="font-size: 8px; margin-top: 40px; text-align: right;">
                    <p style="margin-bottom: 5px;">This notice is provided pursuant to state eviction laws and does not constitute legal advice.</p>
                    <p style="margin-bottom: 5px;">Please consult with an attorney for legal guidance regarding your rights and obligations.</p>
                    <p style="margin-bottom: 5px;">This notice is being served in accordance with the lease agreement dated ${formData.lease.startDate}.</p>
                </div>
            </div>
        `;
    };

    const generatePDF = () => {
        setIsLoading(true);
        const doc = new jsPDF();
        
        // Set initial position and spacing
        let yPos = 20;
        const leftMargin = 20;
        const rightMargin = 190;
        const lineHeight = 5; // Reduced from 6 for more compact layout
        
        // Add header
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.text('NOTICE TO VACATE', 105, yPos + 12, { align: 'center' });
        
        // Add subtitle
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        yPos += 22;
        doc.text('Pursuant to State Eviction Laws', 105, yPos, { align: 'center' });
        
        // Add separator line
        yPos += 6;
        doc.setLineWidth(0.5);
        doc.line(leftMargin, yPos, rightMargin, yPos);
        
        // Add date and recipient info with increased font size
        yPos += 12;
        doc.setFontSize(11);
        doc.text(`Date: ${formData.notice.date}`, leftMargin, yPos);
        yPos += lineHeight; // Reduced from 1.2
        
        // Add all tenant names on the same line
        const allTenantNames = formData.tenant.names.split('\n').map(name => name.trim()).join(', ');
        doc.text(`To: ${allTenantNames}`, leftMargin, yPos);
        yPos += lineHeight;
        
        // Add property address with proper spacing
        yPos += lineHeight;
        const propertyAddress = `Property Address: ${formData.property.address}, ${formData.property.city}, ${formData.property.state} ${formData.property.zip}`;
        const splitAddress = doc.splitTextToSize(propertyAddress, rightMargin - leftMargin);
        doc.text(splitAddress, leftMargin, yPos);
        yPos += (splitAddress.length * lineHeight);
        
        // Add greeting with minimal spacing
        yPos += lineHeight;
        doc.text(`Dear ${allTenantNames.split(',')[0]},`, leftMargin, yPos);
        yPos += lineHeight; // Add one line after Dear
        
        // Add main content with minimal spacing
        yPos += lineHeight;
        const mainContent = formData.landlord.isRepresentative ? 
            `I represent ${formData.landlord.name}, the Owners (hereinafter "my client(s)") of the premises located at ${propertyAddress} (the "Property"), which you currently occupy under a rental agreement.` :
            `I am the Owner of the premises located at ${propertyAddress} (the "Property"), which you currently occupy under a rental agreement.`;
        const splitMainContent = doc.splitTextToSize(mainContent, rightMargin - leftMargin);
        doc.text(splitMainContent, leftMargin, yPos);
        yPos += (splitMainContent.length * lineHeight);
        
        // Add violation details with minimal spacing
        yPos += lineHeight;
        const violationDetails = formData.notice.details;
        const splitViolationDetails = doc.splitTextToSize(violationDetails, rightMargin - leftMargin);
        doc.text(splitViolationDetails, leftMargin, yPos);
        yPos += (splitViolationDetails.length * lineHeight);
        
        // Add second paragraph with minimal spacing
        yPos += lineHeight;
        const secondParagraph = formData.landlord.isRepresentative ?
            `My client has attempted to resolve this matter amicably, however, due to your failure to vacate, my client is forced to take legal action to take possession of the property. Currently, this eviction lawsuit has not been filed and is not public record.` :
            `I have attempted to resolve this matter amicably, however, due to your failure to vacate, I am forced to take legal action to take possession of the property. Currently, this eviction lawsuit has not been filed and is not public record.`;
        const splitSecondParagraph = doc.splitTextToSize(secondParagraph, rightMargin - leftMargin);
        doc.text(splitSecondParagraph, leftMargin, yPos);
        yPos += (splitSecondParagraph.length * lineHeight);
        
        // Add third paragraph with minimal spacing
        yPos += lineHeight;
        const thirdParagraph = formData.landlord.isRepresentative ?
            `Please note that this letter is intended in good faith to resolve the issues surrounding your tenancy. Currently your failure to vacate as required is strictly a matter with your landlord, however, should we not be able to resolve this matter with an agreement to vacate, I will advise my client to move full speed ahead, file the enclosed lawsuit with the Courts and request the Court issue an Order for your removal from the property, resulting in an eviction on your record.` :
            `Please note that this letter is intended in good faith to resolve the issues surrounding your tenancy. Currently your failure to vacate as required is strictly a matter between us, however, should we not be able to resolve this matter with an agreement to vacate, I will move full speed ahead, file the enclosed lawsuit with the Courts and request the Court issue an Order for your removal from the property, resulting in an eviction on your record.`;
        const splitThirdParagraph = doc.splitTextToSize(thirdParagraph, rightMargin - leftMargin);
        doc.text(splitThirdParagraph, leftMargin, yPos);
        yPos += (splitThirdParagraph.length * lineHeight);
        
        // Add fourth paragraph with minimal spacing
        yPos += lineHeight;
        const fourthParagraph = `If you wish to discuss this matter, please respond to my office by the deadline stated in the Summons.`;
        const splitFourthParagraph = doc.splitTextToSize(fourthParagraph, rightMargin - leftMargin);
        doc.text(splitFourthParagraph, leftMargin, yPos);
        yPos += (splitFourthParagraph.length * lineHeight);
        
        // Add bold text with minimal spacing
        yPos += lineHeight;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text(formData.landlord.isRepresentative ? 
            'IF YOU HAVE ANY QUESTIONS, PLEASE DO NOT HESITATE TO CONTACT OUR OFFICE.' : 
            'IF YOU HAVE ANY QUESTIONS, PLEASE DO NOT HESITATE TO CONTACT ME.', 
            leftMargin, yPos);
        
        // Add closing with extra spacing
        yPos += lineHeight * 2; // Add one line before "Thank you"
        doc.setFont('helvetica', 'normal');
        doc.text('Thank you for your prompt attention to this matter,', leftMargin, yPos);
        yPos += lineHeight * 2; // Add one line after "Thank you"
        
        // Add signature block with minimal spacing
        yPos += lineHeight;
        doc.text('Sincerely,', leftMargin, yPos);
        yPos += lineHeight * 2; // Add two lines after Sincerely
        
        // Add signature line
        doc.setLineWidth(0.5);
        doc.line(leftMargin, yPos, leftMargin + 100, yPos);
        yPos += 4;
        
        // Add signature and contact info with minimal spacing
        const signatureName = formData.landlord.isRepresentative ? 
            (formData.landlord.representativeName || formData.landlord.name) : 
            formData.landlord.name;
        doc.text(signatureName, leftMargin, yPos);
        yPos += lineHeight * 1.5; // Keep extra space after FROM
        doc.text(`${formData.landlord.address}`, leftMargin, yPos);
        yPos += lineHeight;
        doc.text(`${formData.landlord.city}, ${formData.landlord.state} ${formData.landlord.zip}`, leftMargin, yPos);
        yPos += lineHeight;
        doc.text(`Phone: ${formData.landlord.phone}`, leftMargin, yPos);
        yPos += lineHeight;
        doc.text(`Email: ${formData.landlord.email}`, leftMargin, yPos);
        yPos += lineHeight * 2; // Add two lines after email
        
        // Add legal disclaimers with minimal spacing
        yPos += lineHeight * 1.5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const disclaimers = [
            'This notice is provided pursuant to state eviction laws and does not constitute legal advice.',
            'Please consult with an attorney for legal guidance regarding your rights and obligations.',
            `This notice is being served in accordance with the lease agreement dated ${formData.lease.startDate}.`
        ];
        
        disclaimers.forEach(disclaimer => {
            const splitText = doc.splitTextToSize(disclaimer, rightMargin - leftMargin);
            doc.text(splitText, leftMargin, yPos);
            yPos += (splitText.length * lineHeight);
        });
        
        // Save the PDF
        doc.save('eviction_notice.pdf');
        setIsLoading(false);
    };

    // Update handlePreview to just regenerate content
    const handlePreview = () => {
        setPreviewContent(generateLetterContent());
    };

    // Update nextStep to remove preview visibility logic
    const nextStep = () => {
        if (currentStep < 4) {
            setCurrentStep(prev => prev + 1);
        }
    };

    // Update prevStep to remove preview visibility logic
    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
        }
    };

    // Validation function
    const validateField = (name, value) => {
        const rules = validationRules[name];
        if (!rules) return '';

        if (rules.required && !value) {
            return 'This field is required';
        }

        if (rules.minLength && value.length < rules.minLength) {
            return `Minimum ${rules.minLength} characters required`;
        }

        if (rules.pattern && !rules.pattern.test(value)) {
            return 'Invalid format';
        }

        if (rules.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            return 'Invalid email address';
        }

        if (rules.numeric && isNaN(value)) {
            return 'Must be a number';
        }

        if (rules.min && Number(value) < rules.min) {
            return `Must be at least ${rules.min}`;
        }

        return '';
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            landlord: {
                isRepresentative: true,
                name: '',
                representativeName: '',
                address: '',
                city: '',
                state: '',
                zip: '',
                phone: '',
                email: ''
            },
            tenant: {
                names: '',
                address: '',
                city: '',
                state: '',
                zip: ''
            },
            property: {
                address: '',
                city: '',
                state: '',
                zip: ''
            },
            lease: {
                startDate: '',
                endDate: '',
                rentAmount: '',
                rentDueDate: ''
            },
            violation: {
                reason: 'nonpayment',
                description: '',
                otherDescription: ''
            },
            notice: {
                date: new Date().toISOString().split('T')[0],
                details: ''
            }
        });
        setCurrentStep(0);
    };

    // Prefill form
    const prefillForm = () => {
        const sampleData = {
            landlord: {
                isRepresentative: true,
                name: 'John Smith',
                representativeName: 'Jane Attorney',
                address: '456 Landlord Lane',
                city: 'Los Angeles',
                state: 'CA',
                zip: '90001',
                phone: '(555) 123-4567',
                email: 'john.smith@example.com'
            },
            tenant: {
                names: 'Jane Doe\nRobert Doe\nSarah Doe',
                address: '789 Tenant Street',
                city: 'Los Angeles',
                state: 'CA',
                zip: '90002'
            },
            property: {
                address: '123 Property Avenue',
                city: 'Los Angeles',
                state: 'CA',
                zip: '90003'
            },
            lease: {
                startDate: '2024-01-01',
                endDate: '2024-12-31',
                rentAmount: '$2,500.00',
                rentDueDate: '2024-01-15'
            },
            violation: {
                reason: 'nonpayment',
                description: '',
                otherDescription: ''
            },
            notice: {
                date: new Date().toISOString().split('T')[0],
                details: `This notice is being served due to non-payment of rent. The total amount owed is $2,500.00. The rent was due on 2024-02-03. Please pay the full amount within 3 days or vacate the premises. If you fail to pay or vacate, legal action will be taken.`
            }
        };

        setFormData(sampleData);
        setCurrentStep(1);
    };

    // Print letter
    const printLetter = () => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Eviction Notice</title>
                    <style>
                        body { font-family: 'Times New Roman', Times, serif; }
                        @media print {
                            @page { margin: 1in; }
                            body { margin: 0; }
                        }
                    </style>
                </head>
                <body>
                    ${letterPreviewRef.current.innerHTML}
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => printWindow.print(), 500);
    };

    return (
        <>
            <div className="header" style={{ backgroundColor: '#0A66C2', color: 'white' }}>
                <h1>Eviction Notice Generator</h1>
                <p>Create professional eviction notices in minutes with our easy-to-use form</p>
            </div>

            <div className="container">
                <div className="disclaimer">
                    <i className="fas fa-exclamation-triangle"></i>
                    <div>
                        <strong>Legal Disclaimer:</strong> This eviction notice generator is for informational purposes only. 
                        Please consult with a legal professional to ensure compliance with local laws and regulations.
                    </div>
                </div>

                <div className="progress-bar">
                    <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>1</div>
                    <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>2</div>
                    <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>3</div>
                    <div className={`step ${currentStep >= 4 ? 'active' : ''}`}>4</div>
                </div>

                <div className="form-container">
                    {currentStep === 1 && (
                        <div className="form-section">
                            <h2>Landlord Information</h2>
                            <div className="form-group">
                                <label className="toggle-label">
                                    <input
                                        type="checkbox"
                                        checked={formData.landlord.isRepresentative}
                                        onChange={(e) => handleInputChange('landlord', 'isRepresentative', e.target.checked)}
                                    />
                                    Are you filing this eviction on behalf of someone?
                                </label>
                            </div>
                            <div className="form-group">
                                <label htmlFor="landlordName">
                                    {formData.landlord.isRepresentative ? 'Landlord Name' : 'Full Name'}
                                </label>
                                <input
                                    type="text"
                                    id="landlordName"
                                    value={formData.landlord.name}
                                    onChange={(e) => handleInputChange('landlord', 'name', e.target.value)}
                                />
                            </div>
                            {formData.landlord.isRepresentative && (
                                <div className="form-group">
                                    <label htmlFor="representativeName">Representative Name</label>
                                    <input
                                        type="text"
                                        id="representativeName"
                                        value={formData.landlord.representativeName}
                                        onChange={(e) => handleInputChange('landlord', 'representativeName', e.target.value)}
                                    />
                                </div>
                            )}
                            <div className="form-group">
                                <label htmlFor="landlordAddress">Address</label>
                                <input
                                    type="text"
                                    id="landlordAddress"
                                    value={formData.landlord.address}
                                    onChange={(e) => handleInputChange('landlord', 'address', e.target.value)}
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="landlordCity">City</label>
                                    <div className="city-input-container">
                                        <input
                                            type="text"
                                            id="landlordCity"
                                            value={formData.landlord.city}
                                            onChange={(e) => handleInputChange('landlord', 'city', e.target.value)}
                                            placeholder="Start typing city name..."
                                        />
                                        {citySuggestions.landlord.length > 0 && (
                                            <ul className="city-suggestions">
                                                {citySuggestions.landlord.map((city, index) => (
                                                    <li
                                                        key={index}
                                                        onClick={() => handleCitySelect('landlord', city)}
                                                    >
                                                        {city.name}, {city.state}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="landlordState">State</label>
                                    <select
                                        id="landlordState"
                                        value={formData.landlord.state}
                                        onChange={(e) => handleInputChange('landlord', 'state', e.target.value)}
                                    >
                                        <option value="">Select State</option>
                                        {usStates.map(state => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="landlordZip">ZIP Code</label>
                                    <input
                                        type="text"
                                        id="landlordZip"
                                        value={formData.landlord.zip}
                                        onChange={(e) => handleInputChange('landlord', 'zip', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="landlordPhone">Phone</label>
                                    <input
                                        type="tel"
                                        id="landlordPhone"
                                        value={formData.landlord.phone}
                                        onChange={(e) => handleInputChange('landlord', 'phone', e.target.value)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="landlordEmail">Email</label>
                                    <input
                                        type="email"
                                        id="landlordEmail"
                                        value={formData.landlord.email}
                                        onChange={(e) => handleInputChange('landlord', 'email', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {currentStep === 2 && (
                        <div className="form-section">
                            <h2>Tenant Information</h2>
                            <div className="form-group">
                                <label htmlFor="tenantNames">Tenant Names (one per line)</label>
                                <textarea
                                    id="tenantNames"
                                    value={formData.tenant.names}
                                    onChange={(e) => handleInputChange('tenant', 'names', e.target.value)}
                                    rows="3"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="tenantAddress">Address</label>
                                <input
                                    type="text"
                                    id="tenantAddress"
                                    value={formData.tenant.address}
                                    onChange={(e) => handleInputChange('tenant', 'address', e.target.value)}
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="tenantCity">City</label>
                                    <div className="city-input-container">
                                        <input
                                            type="text"
                                            id="tenantCity"
                                            value={formData.tenant.city}
                                            onChange={(e) => handleInputChange('tenant', 'city', e.target.value)}
                                            placeholder="Start typing city name..."
                                        />
                                        {citySuggestions.tenant.length > 0 && (
                                            <ul className="city-suggestions">
                                                {citySuggestions.tenant.map((city, index) => (
                                                    <li
                                                        key={index}
                                                        onClick={() => handleCitySelect('tenant', city)}
                                                    >
                                                        {city.name}, {city.state}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="tenantState">State</label>
                                    <select
                                        id="tenantState"
                                        value={formData.tenant.state}
                                        onChange={(e) => handleInputChange('tenant', 'state', e.target.value)}
                                    >
                                        <option value="">Select State</option>
                                        {usStates.map(state => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="tenantZip">ZIP Code</label>
                                    <input
                                        type="text"
                                        id="tenantZip"
                                        value={formData.tenant.zip}
                                        onChange={(e) => handleInputChange('tenant', 'zip', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {currentStep === 3 && (
                        <div className="form-section">
                            <h2>Property Information</h2>
                            <div className="form-group">
                                <label htmlFor="propertyAddress">Property Address</label>
                                <input
                                    type="text"
                                    id="propertyAddress"
                                    value={formData.property.address}
                                    onChange={(e) => handleInputChange('property', 'address', e.target.value)}
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="propertyCity">City</label>
                                    <div className="city-input-container">
                                        <input
                                            type="text"
                                            id="propertyCity"
                                            value={formData.property.city}
                                            onChange={(e) => handleInputChange('property', 'city', e.target.value)}
                                            placeholder="Start typing city name..."
                                        />
                                        {citySuggestions.property.length > 0 && (
                                            <ul className="city-suggestions">
                                                {citySuggestions.property.map((city, index) => (
                                                    <li
                                                        key={index}
                                                        onClick={() => handleCitySelect('property', city)}
                                                    >
                                                        {city.name}, {city.state}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="propertyState">State</label>
                                    <select
                                        id="propertyState"
                                        value={formData.property.state}
                                        onChange={(e) => handleInputChange('property', 'state', e.target.value)}
                                    >
                                        <option value="">Select State</option>
                                        {usStates.map(state => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="propertyZip">ZIP Code</label>
                                    <input
                                        type="text"
                                        id="propertyZip"
                                        value={formData.property.zip}
                                        onChange={(e) => handleInputChange('property', 'zip', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {currentStep === 4 && (
                        <div className="form-section">
                            <h2>Notice Details</h2>
                            <div className="form-group">
                                <label htmlFor="violationReason">Reason for Eviction</label>
                                <select
                                    id="violationReason"
                                    value={formData.violation.reason}
                                    onChange={(e) => handleInputChange('violation', 'reason', e.target.value)}
                                >
                                    <option value="">Select a reason</option>
                                    <option value="nonpayment">Nonpayment of rent</option>
                                    <option value="violations">Lease violations</option>
                                    <option value="damage">Property damage</option>
                                    <option value="illegal">Illegal activity</option>
                                    <option value="expiration">Lease expiration/non-renewal</option>
                                    <option value="occupancy">Owner wishes to occupy property</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="noticeDate">Notice Date</label>
                                <input
                                    type="date"
                                    id="noticeDate"
                                    value={formData.notice.date}
                                    onChange={(e) => handleInputChange('notice', 'date', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="noticeDetails">Additional Details</label>
                                <textarea
                                    id="noticeDetails"
                                    value={formData.notice.details}
                                    onChange={(e) => handleInputChange('notice', 'details', e.target.value)}
                                    rows="4"
                                />
                            </div>
                        </div>
                    )}

                    <div className="form-navigation">
                        <div className="form-navigation-left">
                            {currentStep > 1 && (
                                <button onClick={prevStep} className="btn-secondary">
                                    Previous
                                </button>
                            )}
                        </div>
                        {currentStep < 4 ? (
                            <button onClick={nextStep} className="btn-primary">
                                Next
                            </button>
                        ) : (
                            <button onClick={handlePreview} className="btn-primary">
                                Preview
                            </button>
                        )}
                    </div>
                </div>

                {/* Always show preview section */}
                <div className="preview-container">
                    <h2>Preview Your Eviction Letter</h2>
                    <div
                        className="preview-content"
                        dangerouslySetInnerHTML={{ __html: previewContent }}
                    />
                    <div className="preview-actions">
                        <button onClick={generatePDF} className="btn-primary" disabled={isLoading}>
                            {isLoading ? 'Generating PDF...' : 'Download PDF'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

// Add debounce utility function
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

export default App; 